import { Button } from "../components/ui/button";
import { ThemeToggle } from "../components/ui/theme-toggle";
import { ReactComponent as Logo } from "../assets/icons/logo.svg";
import Arrow from "../assets/icons/arrow_up.svg";
import Verrif from "../assets/icons/verified.svg";
import unVerrif from "../assets/icons/unverified.svg";
import PlusIcon from "../assets/icons/plus_icon.svg";
import SendIcon from "../assets/icons/send_up.svg";
import DepositIcon from "../assets/icons/deposit_icon.svg";
import SellIcon from "../assets/icons/sell_minus.svg";
import RecieveIcon from "../assets/icons/recieve_icon.svg";
import WithIcon from "../assets/icons/withdrawl_icon.svg";
import EightyPayIcon from "../assets/icons/80pay_icon.svg";
import AutostackIcon from "../assets/icons/plus_icon.svg";
import Bell from "../assets/icons/bell.svg";
import Settings from "../assets/icons/settings.svg";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "../components/ui/navigation-menu";
import { Separator } from "../components/ui/separator";

import { cn } from "../lib/utils";
import {
  ChevronRight,
  User,
  LogOut,
  LogIn,
  ArrowRight,
  Menu,
  X,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import React, { useEffect, useState } from "react";
import {
  useActiveAccount,
  useNotifications,
  useSubAccounts,
  useFeatureFlagsByCategory,
} from "../hooks/query/queries";
import {
  useActiveBusiness,
  useSetActiveBusiness,
} from "../hooks/query/dposQueries";
import { setAccount } from "../services/settingsService";
import { useNotificationStore } from "../store/useNotifications";
import LiveTransactionIndicator from "../components/LiveTransactions/LiveTransactionIndicator";
import businessDashboardIcon from "../assets/images/businessSuite/dashboard.svg";
import business88PayIcon from "../assets/images/businessSuite/88pay.svg";
import businessReceiveIcon from "../assets/images/businessSuite/receive.svg";
import businessDPOSIcon from "../assets/images/businessSuite/dpos.svg";
import businessSendIcon from "../assets/images/businessSuite/send.svg";
import businessPlusIcon from "../assets/images/businessSuite/plus.svg";
import businessDepositIcon from "../assets/images/businessSuite/deposit.svg";
import businessLinkIcon from "../assets/images/businessSuite/payLink.svg";
import businessAccountIcon from "../assets/images/businessSuite/businessSuite.svg";
import businessWithdrawIcon from "../assets/images/businessSuite/withdraw.svg";
import HeaderAccountSwitcher from "../components/HeaderAccountSwitcher";

const menuItemStyle =
  "group text-text-muted inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 font-semibold transition-colors hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground disabled:pointer-events-none disabled:opacity-50 cursor-pointer text-[16px] text-[#9D96AF]";

export default function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user: userProfile, logout: handleLogout, refreshUser } = useAuth();
  const userId = userProfile?._id;
  const accountId = userProfile?.accountId;
  const { data: notis } = useNotifications();
  const setNotifications = useNotificationStore((s) => s?.setNotifications);
  const storeUnreadCount = useNotificationStore((s) => s?.unreadCount);
  // Use query data directly as fallback if store hasn't been updated yet
  const unreadCount = storeUnreadCount || notis?.totalUnread || 0;
  const { data: activeAccount, isLoading: loadingActive } = useActiveAccount(
    userId,
    accountId
  );
  const [loadingAcc, setLoadingAcc] = useState(false);
  const {
    data: accounts,
    isLoading: loadingAccounts,
    isError: accountsError,
  } = useSubAccounts(userId);

  const [isLoading, setIsLoading] = useState(true);
  const { data: featureFlags } = useFeatureFlagsByCategory("mainFeatures");
  const { data: settingsPageFlags } = useFeatureFlagsByCategory("settingsPage");
  const subAccountsFeatureEnabled = settingsPageFlags?.subAccounts === true;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isTradingMenuOpen, setIsTradingMenuOpen] = useState(false);
  const [isBusinessMenuOpen, setIsBusinessMenuOpen] = useState(false);
  const [overlayActive, setOverlayActive] = useState(false);
  const [isVeriffVerified, setIsVeriffVerified] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownOpenBusiness, setDropdownOpenBusiness] = useState(false);
  const {
    businesses,
    activeBusinessId,
    hasSelectedBusiness,
    isLoading: isBusinessContextLoading,
  } = useActiveBusiness();
  const setActiveBusinessMutation = useSetActiveBusiness();

  useEffect(() => {
    setTimeout(() => {
      setIsLoading(false);
    }, 2000);
  }, []);
  useEffect(() => {
    if (notis?.results) {
      setNotifications(notis.results, notis.totalUnread);
    }
  }, [notis, setNotifications]);

  const switchAccount = async (
    nextAccountId,
    targetPath = "/trading/digitalWallet"
  ) => {
    setLoadingAcc(true);
    try {
      await setAccount(userId, nextAccountId);
      await refreshUser();
      navigate(targetPath);
    } catch (error) {
      console.error("Error setting account:", error.message);
    } finally {
      setLoadingAcc(false);
    }
  };

  const handlePersonalContextSelect = async () => {
    const mainAccount = accounts?.find((account) => account?.isMainAccount);
    const mainAccountId = mainAccount?._id || mainAccount?.accountId;
    const activeAccountIdentifier =
      activeAccount?._id || activeAccount?.accountId;
    const shouldSwitchAccount =
      Boolean(mainAccountId) && mainAccountId !== activeAccountIdentifier;
    const shouldClearBusiness = Boolean(activeBusinessId);

    if (!shouldSwitchAccount && !shouldClearBusiness) {
      return;
    }

    try {
      if (shouldClearBusiness) {
        await setActiveBusinessMutation.mutateAsync(null);
      }

      if (shouldSwitchAccount && mainAccountId) {
        await switchAccount(mainAccountId);
        return;
      }

      navigate("/trading/digitalWallet");
    } catch (error) {
      console.error("Error selecting personal account:", error.message);
    }
  };

  const handleSubAccountContextSelect = async (account) => {
    const nextAccountId = account?._id || account?.accountId;
    const activeAccountIdentifier =
      activeAccount?._id || activeAccount?.accountId;

    if (!nextAccountId) {
      return;
    }

    try {
      if (activeBusinessId) {
        await setActiveBusinessMutation.mutateAsync(null);
      }

      if (nextAccountId === activeAccountIdentifier && !activeBusinessId) {
        return;
      }

      await switchAccount(nextAccountId);
    } catch (error) {
      console.error("Error selecting sub account:", error.message);
    }
  };

  const handleBusinessContextSelect = async (businessId) => {
    if (
      businessId === activeBusinessId ||
      setActiveBusinessMutation.isPending
    ) {
      return;
    }

    try {
      await setActiveBusinessMutation.mutateAsync(businessId);
      navigate("/trading/business-suite");
    } catch (error) {
      console.error("Error selecting business profile:", error.message);
    }
  };

  useEffect(() => {
    if (userProfile) {
      const kycStatus =
        userProfile?.veriffDetails?.status || userProfile.kycStatus;
      setIsVeriffVerified(kycStatus === "approved");
    }
  }, [userProfile]);

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsTradingMenuOpen(false);
    setIsBusinessMenuOpen(false);
  };

  const isActivePath = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  const getMenuItemClassName = (path) => {
    const isActive = isActivePath(path);
    return cn(menuItemStyle, isActive && "text-[#423E51]");
  };

  const isBusinessSuiteNavActive =
    location.pathname === "/trading/business-suite";
  const showBusinessSuiteNavigation = hasSelectedBusiness;
  const showDigitalWalletNavigation = !hasSelectedBusiness;

  const user = {};

  const digitalWalletItems = [
    featureFlags?.buyCrypto && {
      title: "Buy",
      description: "Add crypto to your wallet",
      icon: PlusIcon,
      link: "/trading/digitalWallet?tab=buy-sell&childTab=Buy",
    },
    featureFlags?.sellCrypto && {
      title: "Sell",
      description: "Sell crypto from your wallet",
      icon: SellIcon,
      link: "/trading/digitalWallet?tab=buy-sell&childTab=Sell",
    },
    featureFlags?.sendCrypto && {
      title: "Send",
      description: "Send crypto to a wallet",
      icon: SendIcon,
      link: "/trading/digitalWallet?tab=send-receive&childTab=send",
    },
    featureFlags?.receiveCrypto && {
      title: "Receive",
      description: "Receive crypto to your wallet",
      icon: RecieveIcon,
      link: "/trading/digitalWallet?tab=send-receive&childTab=receive",
    },
    featureFlags?.deposit && {
      title: "Deposit",
      description: "Deposit fiat into your wallet",
      icon: DepositIcon,
      link: "/trading/digitalWallet?tab=withdraw-deposit&childTab=deposit",
    },
    featureFlags?.withdraw && {
      title: "Withdraw",
      description: "Withdraw fiat from your wallet",
      icon: WithIcon,
      link: "/trading/digitalWallet?tab=withdraw-deposit&childTab=withdraw",
    },
    featureFlags?.eightPay && {
      title: "80eightPay",
      description: "Send and receive money",
      icon: EightyPayIcon,
      link: "/trading/digitalWallet?tab=80eightpay",
    },
    featureFlags?.autostack && {
      title: "Autostack",
      description: "Create a new autostack",
      icon: AutostackIcon,
      link: "/autostack",
    },
  ].filter(Boolean);

  const businessSuiteMobileItems = [
    {
      title: "Dashboard",
      description: "View and manage analytics",
      to: "/trading/business-suite",
      img: businessDashboardIcon,
    },
    featureFlags?.eightPay && {
      title: "88Pay",
      description: "Send and receive money",
      to: "/trading/business-suite?tab=80eightpay",
      state: undefined,
      img: business88PayIcon,
    },
    featureFlags?.receiveCrypto && {
      title: "Receive",
      description: "Receive crypto to your wallet",
      to: "/trading/business-suite?tab=send-receive",
      state: { childTab: "receive" },
      img: businessReceiveIcon,
    },
    {
      title: "DPOS",
      description: "Receive crypto to your wallet",
      to: "/trading/business-suite?tab=dpos",
      img: businessDPOSIcon,
    },
    featureFlags?.sendCrypto && {
      title: "Send",
      description: "Send crypto to a wallet",
      to: "/trading/business-suite?tab=send-receive",
      state: { childTab: "send" },
      img: businessSendIcon,
    },
    featureFlags?.buyCrypto && {
      title: "Buy",
      description: "Add crypto to your wallet",
      to: "/trading/business-suite?tab=buy-sell",
      state: { childTab: "Buy" },
      img: PlusIcon,
    },
    featureFlags?.sellCrypto && {
      title: "Sell",
      description: "Sell crypto from your wallet",
      to: "/trading/business-suite?tab=buy-sell",
      state: { childTab: "Sell" },
      img: PlusIcon,
    },
    featureFlags?.zar8Yield && {
      title: "ZAR8 Yield",
      description: "Access your ZAR8 Yield dashboard",
      to: "/zar8",
      img: businessPlusIcon,
    },
    featureFlags?.deposit && {
      title: "Deposit",
      description: "Deposit fiat into your wallet",
      to: "/trading/business-suite?tab=withdraw-deposit",
      state: { childTab: "deposit" },
      img: DepositIcon,
    },
    featureFlags?.withdraw && {
      title: "Withdraw",
      description: "Withdraw fiat from your wallet",
      to: "/trading/business-suite?tab=withdraw-deposit",
      state: { childTab: "withdraw" },
      img: WithIcon,
    },
    featureFlags?.eightPay && {
      title: "Pay link",
      description: "Accept payments globally",
      to: "/trading/business-suite?tab=pay-link",
      state: undefined,
      img: businessLinkIcon,
    },
  ].filter(Boolean);

  return (
    <>
      <header className="border-b border-border px-4 md:px-14 bg-background transition-colors duration-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-10 h-24">
            <Link to="/">
              <Logo />
            </Link>

            <NavigationMenu className="hidden lg:block">
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link to="/" className={getMenuItemClassName("/")}>
                      Home
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>
                {showDigitalWalletNavigation && (
                  <NavigationMenuItem>
                    <DropdownMenu
                      open={dropdownOpen}
                      onOpenChange={setDropdownOpen}
                    >
                      <DropdownMenuTrigger asChild>
                        <NavigationMenuTrigger
                          className={cn(
                            menuItemStyle,
                            (isActivePath("/trading/digitalWallet") ||
                              isActivePath("/autostack")) &&
                              "text-[#423E51]"
                          )}
                        >
                          Digital Wallet
                        </NavigationMenuTrigger>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        sideOffset={8}
                        className="flex items-center px-8 py-5 min-w-[100vw] bg-background border-border"
                      >
                        <div className="flex justify-center w-[20%]">
                          <Link to="/trading/digitalWallet">
                            <div className="min-h-[240px] min-w-[240px] rounded-[16px] bg-[linear-gradient(86.72deg,#57249C_-10.66%,#EB3366_113.47%)] px-4 py-10 flex flex-col justify-end">
                              <img
                                src={Arrow}
                                className="h-[35px] w-[35px] mb-2"
                                alt="Arrow"
                              />
                              <p className="text-white text-[16px] font-semibold mb-2">
                                Digital Wallet
                              </p>
                              <p className="text-white text-[14px] ">
                                Fund and manage your{" "}
                              </p>
                              <p className="text-white text-[14px] ">
                                80eight assets, including
                              </p>
                              <p className="text-white text-[14px] ">
                                crypto and investments.
                              </p>
                            </div>
                          </Link>
                        </div>
                        <div className="w-[80%] h-[100%]">
                          <div className="grid grid-cols-3 py-4">
                            {featureFlags?.buyCrypto && (
                              <DropdownMenuItem
                                asChild
                                className="justify-between px-6 rounded-full cursor-pointer hover:bg-gray-50"
                              >
                                <Link to="/trading/digitalWallet?tab=buy-sell&childTab=Buy">
                                  <div className="flex">
                                    <img src={PlusIcon} alt="PlusIcon" />
                                    <div className="ml-2">
                                      <p className="text-[16px] text-text-primary font-semibold mb-1">
                                        Buy
                                      </p>
                                      <p className="text-[14px] text-text-secondary font-normal">
                                        Add crypto to your wallet
                                      </p>
                                    </div>
                                  </div>
                                  <div className="border rounded-full p-[10px]">
                                    <ArrowRight />
                                  </div>
                                </Link>
                              </DropdownMenuItem>
                            )}
                            {featureFlags?.sendCrypto && (
                              <DropdownMenuItem
                                asChild
                                className="justify-between px-6 rounded-full cursor-pointer hover:bg-gray-50"
                              >
                                <Link to="/trading/digitalWallet?tab=send-receive&childTab=send">
                                  <div className="flex">
                                    <img src={SendIcon} alt="SendIcon" />
                                    <div className="ml-2">
                                      <p className="text-[16px] text-text-primary font-semibold mb-1">
                                        Send
                                      </p>
                                      <p className="text-[14px] text-text-secondary font-normal">
                                        Send crypto to a wallet
                                      </p>
                                    </div>
                                  </div>
                                  <div className="border rounded-full p-[10px]">
                                    <ArrowRight />
                                  </div>
                                </Link>
                              </DropdownMenuItem>
                            )}
                            {featureFlags?.deposit && (
                              <DropdownMenuItem
                                asChild
                                className="justify-between px-6 rounded-full cursor-pointer hover:bg-gray-50"
                              >
                                <Link to="/trading/digitalWallet?tab=withdraw-deposit&childTab=deposit">
                                  <div className="flex">
                                    <img src={DepositIcon} alt="DepositIcon" />
                                    <div className="ml-2">
                                      <p className="text-[16px] text-text-primary font-semibold mb-1">
                                        Deposit
                                      </p>
                                      <p className="text-[14px] text-text-secondary font-normal">
                                        Deposit fiat into your wallet
                                      </p>
                                    </div>
                                  </div>
                                  <div className="border rounded-full p-[10px]">
                                    <ArrowRight />
                                  </div>
                                </Link>
                              </DropdownMenuItem>
                            )}
                          </div>
                          <div className="grid grid-cols-3 py-4">
                            {featureFlags?.sellCrypto && (
                              <DropdownMenuItem
                                asChild
                                className="justify-between px-6 rounded-full cursor-pointer hover:bg-gray-50"
                              >
                                <Link to="/trading/digitalWallet?tab=buy-sell&childTab=Sell">
                                  <div className="flex">
                                    <img src={SellIcon} alt="SellIcon" />
                                    <div className="ml-2">
                                      <p className="text-[16px] text-text-primary font-semibold mb-1">
                                        Sell
                                      </p>
                                      <p className="text-[14px] text-text-secondary font-normal">
                                        Add crypto to your wallet
                                      </p>
                                    </div>
                                  </div>
                                  <div className="border rounded-full p-[10px]">
                                    <ArrowRight />
                                  </div>
                                </Link>
                              </DropdownMenuItem>
                            )}
                            {featureFlags?.receiveCrypto && (
                              <DropdownMenuItem
                                asChild
                                className="justify-between px-6 rounded-full cursor-pointer hover:bg-gray-50"
                              >
                                <Link to="/trading/digitalWallet?tab=send-receive&childTab=receive">
                                  <div className="flex">
                                    <img src={RecieveIcon} alt="RecieveIcon" />
                                    <div className="ml-2">
                                      <p className="text-[16px] text-text-primary font-semibold mb-1">
                                        Receive
                                      </p>
                                      <p className="text-[14px] text-text-secondary font-normal">
                                        Receive crypto to your wallet
                                      </p>
                                    </div>
                                  </div>
                                  <div className="border rounded-full p-[10px]">
                                    <ArrowRight />
                                  </div>
                                </Link>
                              </DropdownMenuItem>
                            )}
                            {featureFlags?.withdraw && (
                              <DropdownMenuItem
                                asChild
                                className="justify-between px-6 rounded-full cursor-pointer hover:bg-gray-50"
                              >
                                <Link to="/trading/digitalWallet?tab=withdraw-deposit&childTab=withdraw">
                                  <div className="flex">
                                    <img src={WithIcon} alt="WithIcon" />
                                    <div className="ml-2">
                                      <p className="text-[16px] text-text-primary font-semibold mb-1">
                                        Withdraw
                                      </p>
                                      <p className="text-[14px] text-text-secondary font-normal">
                                        Withdraw fiat from your wallet
                                      </p>
                                    </div>
                                  </div>
                                  <div className="border rounded-full p-[10px]">
                                    <ArrowRight />
                                  </div>
                                </Link>
                              </DropdownMenuItem>
                            )}
                          </div>
                          <div className="grid grid-cols-3 py-4">
                            {featureFlags?.eightPay && (
                              <DropdownMenuItem
                                asChild
                                className="justify-between px-6 rounded-full cursor-pointer hover:bg-gray-50"
                              >
                                <Link to="/trading/digitalWallet?tab=80eightpay">
                                  <div className="flex">
                                    <img
                                      src={EightyPayIcon}
                                      alt="EightyPayIcon"
                                    />
                                    <div className="ml-2">
                                      <p className="text-[16px] text-text-primary font-semibold mb-1">
                                        80eightPay
                                      </p>
                                      <p className="text-[14px] text-text-secondary font-normal">
                                        Send and receive money
                                      </p>
                                    </div>
                                  </div>
                                  <div className="border rounded-full p-[10px]">
                                    <ArrowRight />
                                  </div>
                                </Link>
                              </DropdownMenuItem>
                            )}
                            {featureFlags?.autostack && (
                              <DropdownMenuItem
                                asChild
                                className="justify-between px-6 rounded-full cursor-pointer hover:bg-gray-50"
                              >
                                <Link to="/autostack">
                                  <div className="flex">
                                    <img src={AutostackIcon} alt="Autostack" />
                                    <div className="ml-2">
                                      <p className="text-[16px] text-text-primary font-semibold mb-1">
                                        Autostack
                                      </p>
                                      <p className="text-[14px] text-text-secondary font-normal">
                                        Create a new autostack
                                      </p>
                                    </div>
                                  </div>
                                  <div className="border rounded-full p-[10px]">
                                    <ArrowRight />
                                  </div>
                                </Link>
                              </DropdownMenuItem>
                            )}
                          </div>
                        </div>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </NavigationMenuItem>
                )}
                {showBusinessSuiteNavigation && (
                  <NavigationMenuItem>
                    <DropdownMenu
                      open={dropdownOpenBusiness}
                      onOpenChange={setDropdownOpenBusiness}
                    >
                      <DropdownMenuTrigger asChild>
                        <NavigationMenuTrigger
                          className={cn(
                            menuItemStyle,
                            isBusinessSuiteNavActive && "text-[#423E51]"
                          )}
                        >
                          Business Suite
                        </NavigationMenuTrigger>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        sideOffset={8}
                        className="flex items-start px-8 py-5 min-w-[100vw] bg-background border-border"
                      >
                        <div className="flex justify-center w-[20%]">
                          <Link to="/trading/business-suite">
                            <div className="h-[240px] w-[240px] rounded-[16px] bg-[linear-gradient(86.72deg,#57249C_-10.66%,#EB3366_113.47%)] px-6 py-8 flex flex-col justify-end lg:mt-6">
                              <img
                                src={businessAccountIcon}
                                alt="Business Account"
                                className="w-[35px] h-[35px] mb-2"
                              />
                              <div>
                                <p className="text-white text-[16px] font-semibold mb-2">
                                  Business Suite
                                </p>
                                <p className="text-white/95 text-[14px] mb-2">
                                  Hub for business financial activities
                                </p>
                                <span className="w-full flex items-center justify-center rounded-full border-[1px] border-white px-4 py-2.5 text-white text-[14px]">
                                  Go to business suite
                                </span>
                              </div>
                            </div>
                          </Link>
                        </div>
                        <div className="w-[80%] h-[100%]">
                          <div className="grid grid-cols-3 py-4">
                            <DropdownMenuItem
                              asChild
                              className="justify-between px-6 rounded-full cursor-pointer hover:bg-gray-50"
                            >
                              <Link to="/trading/business-suite">
                                <div className="flex items-center justify-center min-w-0">
                                  <img
                                    src={businessDashboardIcon}
                                    alt="Business Dashboard"
                                    className="w-[40px] h-[40px]"
                                  />
                                  <div className="ml-3 min-w-0">
                                    <p className="text-[16px] text-text-primary font-semibold mb-1">
                                      Dashboard
                                    </p>
                                    <p className="text-[14px] text-text-secondary font-normal">
                                      View and manage analytics
                                    </p>
                                  </div>
                                </div>
                                <div className="border rounded-full p-[10px] shrink-0">
                                  <ArrowRight />
                                </div>
                              </Link>
                            </DropdownMenuItem>
                            {featureFlags?.sendCrypto && (
                              <DropdownMenuItem
                                asChild
                                className="justify-between px-6 rounded-full cursor-pointer hover:bg-gray-50"
                              >
                                <Link
                                  to="/trading/business-suite?tab=send-receive"
                                  state={{
                                    childTab: "send",
                                  }}
                                >
                                  <div className="flex items-center justify-center min-w-0">
                                    <img
                                      src={businessSendIcon}
                                      alt="Business Send"
                                      className="w-[40px] h-[40px]"
                                    />
                                    <div className="ml-3 min-w-0">
                                      <p className="text-[16px] text-text-primary font-semibold mb-1">
                                        Send
                                      </p>
                                      <p className="text-[14px] text-text-secondary font-normal">
                                        Send crypto to a wallet
                                      </p>
                                    </div>
                                  </div>
                                  <div className="border rounded-full p-[10px] shrink-0">
                                    <ArrowRight />
                                  </div>
                                </Link>
                              </DropdownMenuItem>
                            )}
                            {featureFlags?.deposit && (
                              <DropdownMenuItem
                                asChild
                                className="justify-between px-6 rounded-full cursor-pointer hover:bg-gray-50"
                              >
                                <Link
                                  to="/trading/business-suite?tab=withdraw-deposit"
                                  state={{
                                    childTab: "deposit",
                                  }}
                                >
                                  <div className="flex items-center justify-center min-w-0">
                                    <img
                                      src={businessDepositIcon}
                                      alt="Business Deposit"
                                      className="w-[40px] h-[40px]"
                                    />
                                    <div className="ml-3 min-w-0">
                                      <p className="text-[16px] text-text-primary font-semibold mb-1">
                                        Deposit
                                      </p>
                                      <p className="text-[14px] text-text-secondary font-normal">
                                        Deposit fiat into your wallet
                                      </p>
                                    </div>
                                  </div>
                                  <div className="border rounded-full p-[10px] shrink-0">
                                    <ArrowRight />
                                  </div>
                                </Link>
                              </DropdownMenuItem>
                            )}
                          </div>
                          <div className="grid grid-cols-3 py-4">
                            {featureFlags?.eightPay && (
                              <DropdownMenuItem
                                asChild
                                className="justify-between px-6 rounded-full cursor-pointer hover:bg-gray-50"
                              >
                                <Link to="/trading/business-suite?tab=80eightpay">
                                  <div className="flex items-center justify-center min-w-0">
                                    <img
                                      src={business88PayIcon}
                                      alt="Business 88Pay"
                                      className="w-[40px] h-[40px]"
                                    />
                                    <div className="ml-3 min-w-0">
                                      <p className="text-[16px] text-text-primary font-semibold mb-1">
                                        88Pay
                                      </p>
                                      <p className="text-[14px] text-text-secondary font-normal">
                                        Send and receive money
                                      </p>
                                    </div>
                                  </div>
                                  <div className="border rounded-full p-[10px] shrink-0">
                                    <ArrowRight />
                                  </div>
                                </Link>
                              </DropdownMenuItem>
                            )}
                            {featureFlags?.buyCrypto && (
                              <DropdownMenuItem
                                asChild
                                className="justify-between px-6 rounded-full cursor-pointer hover:bg-gray-50"
                              >
                                <Link
                                  to="/trading/business-suite?tab=buy-sell"
                                  state={{
                                    childTab: "Buy",
                                  }}
                                >
                                  <div className="flex items-center justify-center min-w-0">
                                    <img
                                      src={businessPlusIcon}
                                      alt="Business Buy"
                                      className="w-[40px] h-[40px]"
                                    />
                                    <div className="ml-3 min-w-0">
                                      <p className="text-[16px] text-text-primary font-semibold mb-1">
                                        Buy
                                      </p>
                                      <p className="text-[14px] text-text-secondary font-normal">
                                        Add crypto to your wallet
                                      </p>
                                    </div>
                                  </div>
                                  <div className="border rounded-full p-[10px] shrink-0">
                                    <ArrowRight />
                                  </div>
                                </Link>
                              </DropdownMenuItem>
                            )}
                            {featureFlags?.withdraw && (
                              <DropdownMenuItem
                                asChild
                                className="justify-between px-6 rounded-full cursor-pointer hover:bg-gray-50"
                              >
                                <Link
                                  to="/trading/business-suite?tab=withdraw-deposit"
                                  state={{
                                    childTab: "withdraw",
                                  }}
                                >
                                  <div className="flex items-center justify-center min-w-0">
                                    <img
                                      src={businessWithdrawIcon}
                                      alt="Business Withdraw"
                                      className="w-[40px] h-[40px]"
                                    />
                                    <div className="ml-3 min-w-0">
                                      <p className="text-[16px] text-text-primary font-semibold mb-1">
                                        Withdraw
                                      </p>
                                      <p className="text-[14px] text-text-secondary font-normal">
                                        Withdraw fiat from your wallet
                                      </p>
                                    </div>
                                  </div>
                                  <div className="border rounded-full p-[10px] shrink-0">
                                    <ArrowRight />
                                  </div>
                                </Link>
                              </DropdownMenuItem>
                            )}
                          </div>
                          <div className="grid grid-cols-3 py-4">
                            {featureFlags?.receiveCrypto && (
                              <DropdownMenuItem
                                asChild
                                className="justify-between px-6 rounded-full cursor-pointer hover:bg-gray-50"
                              >
                                <Link
                                  to="/trading/business-suite?tab=send-receive"
                                  state={{
                                    childTab: "receive",
                                  }}
                                >
                                  <div className="flex items-center justify-center min-w-0">
                                    <img
                                      src={businessReceiveIcon}
                                      alt="Business Receive"
                                      className="w-[40px] h-[40px]"
                                    />
                                    <div className="ml-3 min-w-0">
                                      <p className="text-[16px] text-text-primary font-semibold mb-1">
                                        Receive
                                      </p>
                                      <p className="text-[14px] text-text-secondary font-normal">
                                        Receive crypto to your wallet
                                      </p>
                                    </div>
                                  </div>
                                  <div className="border rounded-full p-[10px] shrink-0">
                                    <ArrowRight />
                                  </div>
                                </Link>
                              </DropdownMenuItem>
                            )}
                            {featureFlags?.sellCrypto && (
                              <DropdownMenuItem
                                asChild
                                className="justify-between px-6 rounded-full cursor-pointer hover:bg-gray-50"
                              >
                                <Link
                                  to="/trading/business-suite?tab=buy-sell"
                                  state={{
                                    childTab: "Sell",
                                  }}
                                >
                                  <div className="flex items-center justify-center min-w-0">
                                    <img
                                      src={businessPlusIcon}
                                      alt="Business Sell"
                                      className="w-[40px] h-[40px]"
                                    />
                                    <div className="ml-3 min-w-0">
                                      <p className="text-[16px] text-text-primary font-semibold mb-1">
                                        Sell
                                      </p>
                                      <p className="text-[14px] text-text-secondary font-normal">
                                        Sell crypto from your wallet
                                      </p>
                                    </div>
                                  </div>
                                  <div className="border rounded-full p-[10px] shrink-0">
                                    <ArrowRight />
                                  </div>
                                </Link>
                              </DropdownMenuItem>
                            )}
                            {featureFlags?.eightPay && (
                              <DropdownMenuItem
                                asChild
                                className="justify-between px-6 rounded-full cursor-pointer hover:bg-gray-50"
                              >
                                <Link to="/trading/business-suite?tab=pay-link">
                                  <div className="flex items-center justify-center min-w-0">
                                    <img
                                      src={businessLinkIcon}
                                      alt="Business Pay Link"
                                      className="w-[40px] h-[40px]"
                                    />
                                    <div className="ml-3 min-w-0">
                                      <p className="text-[16px] text-text-primary font-semibold mb-1">
                                        Pay link
                                      </p>
                                      <p className="text-[14px] text-text-secondary font-normal">
                                        Accept payments globally
                                      </p>
                                    </div>
                                  </div>
                                  <div className="border rounded-full p-[10px] shrink-0">
                                    <ArrowRight />
                                  </div>
                                </Link>
                              </DropdownMenuItem>
                            )}
                          </div>
                          <div className="grid grid-cols-3 py-4">
                            <DropdownMenuItem
                              asChild
                              className="justify-between px-6 rounded-full cursor-pointer hover:bg-gray-50"
                            >
                              <Link to="/trading/business-suite?tab=dpos">
                                <div className="flex items-center justify-center min-w-0">
                                  <img
                                    src={businessDPOSIcon}
                                    alt="Business DPOS"
                                    className="w-[40px] h-[40px]"
                                  />
                                  <div className="ml-3 min-w-0">
                                    <p className="text-[16px] text-text-primary font-semibold mb-1">
                                      DPOS
                                    </p>
                                    <p className="text-[14px] text-text-secondary font-normal">
                                      Receive crypto to your wallet
                                    </p>
                                  </div>
                                </div>
                                <div className="border rounded-full p-[10px] shrink-0">
                                  <ArrowRight />
                                </div>
                              </Link>
                            </DropdownMenuItem>
                            {featureFlags?.zar8Yield && (
                              <DropdownMenuItem
                                asChild
                                className="justify-between px-6 rounded-full cursor-pointer hover:bg-gray-50"
                              >
                                <Link to="/zar8">
                                  <div className="flex items-center justify-center min-w-0">
                                    <img
                                      src={businessPlusIcon}
                                      alt="Business ZAR8 Yield"
                                      className="w-[40px] h-[40px]"
                                    />
                                    <div className="ml-3 min-w-0">
                                      <p className="text-[16px] text-text-primary font-semibold mb-1">
                                        ZAR8 Yield
                                      </p>
                                      <p className="text-[14px] text-text-secondary font-normal">
                                        Access your ZAR8 Yield dashboard
                                      </p>
                                    </div>
                                  </div>
                                  <div className="border rounded-full p-[10px] shrink-0">
                                    <ArrowRight />
                                  </div>
                                </Link>
                              </DropdownMenuItem>
                            )}
                            <div className="min-h-[1px]" aria-hidden />
                          </div>
                        </div>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </NavigationMenuItem>
                )}
                {featureFlags?.invest && (
                  <NavigationMenuItem>
                    <NavigationMenuLink asChild>
                      <Link
                        to="/invest"
                        className={getMenuItemClassName("/invest")}
                      >
                        Invest
                      </Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                )}
                {featureFlags?.zar8Yield && (
                  <NavigationMenuItem>
                    <NavigationMenuLink asChild>
                      <Link
                        to="/zar8"
                        className={getMenuItemClassName("/zar8")}
                      >
                        ZAR8
                      </Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                )}
                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link
                      to="/support/index"
                      className={getMenuItemClassName("/support")}
                    >
                      Support
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>
                {/* {(featureFlags?.buyForex ||
                  featureFlags?.sellForex ||
                  featureFlags?.withdrawForex ||
                  featureFlags?.depositForex) && (
                  <NavigationMenuItem>
                    <NavigationMenuLink asChild>
                      <Link
                        to="/forex/forex-dashboard"
                        className={getMenuItemClassName("/forex")}
                      >
                        Forex
                      </Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                )} */}
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          <div className="hidden lg:flex items-center gap-4">
            {/* Live Transaction Indicator */}
            <LiveTransactionIndicator />

            {process.env.REACT_APP_DARK_MODE_ENABLED === "true" && (
              <ThemeToggle />
            )}
            {userProfile?._id ? (
              <HeaderAccountSwitcher
                accounts={accounts || []}
                accountsError={accountsError}
                activeAccountId={activeAccount?._id || activeAccount?.accountId}
                activeBusinessId={activeBusinessId}
                businesses={businesses}
                isAccountLoading={loadingActive || loadingAccounts}
                isAccountSwitching={loadingAcc}
                isBusinessLoading={isBusinessContextLoading}
                isBusinessSwitching={setActiveBusinessMutation.isPending}
                onLogout={() => handleLogout()}
                onSelectBusiness={handleBusinessContextSelect}
                onSelectPersonal={handlePersonalContextSelect}
                onSelectSubAccount={handleSubAccountContextSelect}
                subAccountsEnabled={subAccountsFeatureEnabled}
              />
            ) : (
              !isLoading && (
                <Link to="/api/auth/login">
                  <Button variant="outline" className="rounded-full px-5 h-12">
                    <LogIn className="w-4 h-4 mr-2" /> Login
                  </Button>
                </Link>
              )
            )}
            <Link to="/notifications">
              <Button
                variant="outline"
                size="icon"
                className="relative rounded-full border border-bg-neutral"
              >
                <img src={Bell} alt="Bell" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs text-white">
                    {unreadCount}
                  </span>
                )}
              </Button>
            </Link>
            <Link to="/settings/index">
              <Button
                variant="outline"
                size="icon"
                className="rounded-full border border-bg-neutral"
              >
                <img src={Settings} alt="Settings" />
              </Button>
            </Link>
          </div>

          <div className="lg:hidden flex items-center gap-3">
            {/* Mobile Live Transaction Indicator (icon only) */}
            <LiveTransactionIndicator hideLabel />
            {userProfile?._id ? (
              <HeaderAccountSwitcher
                compact
                accounts={accounts || []}
                accountsError={accountsError}
                activeAccountId={activeAccount?._id || activeAccount?.accountId}
                activeBusinessId={activeBusinessId}
                businesses={businesses}
                isAccountLoading={loadingActive || loadingAccounts}
                isAccountSwitching={loadingAcc}
                isBusinessLoading={isBusinessContextLoading}
                isBusinessSwitching={setActiveBusinessMutation.isPending}
                onLogout={() => handleLogout()}
                onSelectBusiness={handleBusinessContextSelect}
                onSelectPersonal={handlePersonalContextSelect}
                onSelectSubAccount={handleSubAccountContextSelect}
                subAccountsEnabled={subAccountsFeatureEnabled}
              />
            ) : null}
            <Button
              variant="outline"
              size="icon"
              className="rounded-full border border-bg-neutral"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </Button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-24 left-0 right-0 bg-background border-b border-border shadow-lg z-50 max-h-[calc(100vh-6rem)] overflow-y-auto transition-colors duration-200">
            <div className="px-4 py-6 space-y-4">
              <div>
                <Link
                  to="/"
                  className={cn(
                    "block px-4 py-3 font-semibold text-lg hover:bg-accent rounded-md transition-colors duration-200",
                    isActivePath("/") ? "text-[#423E51]" : "text-text-primary"
                  )}
                  onClick={closeMobileMenu}
                >
                  Home
                </Link>
              </div>

              <Separator />

              {showDigitalWalletNavigation && (
                <>
                  <div>
                    <button
                      className={cn(
                        "flex items-center justify-between w-full px-4 py-3 font-semibold text-lg hover:bg-accent rounded-md transition-colors duration-200",
                        isActivePath("/trading/digitalWallet") ||
                          isActivePath("/autostack")
                          ? "text-[#423E51]"
                          : "text-text-primary"
                      )}
                      onClick={() => setIsTradingMenuOpen(!isTradingMenuOpen)}
                    >
                      Digital Wallet
                      <ChevronRight
                        className={`w-5 h-5 transition-transform ${
                          isTradingMenuOpen ? "rotate-90" : ""
                        }`}
                      />
                    </button>
                    {isTradingMenuOpen && (
                      <div className="ml-4 mt-2 space-y-2">
                        <Link
                          to="/trading/digitalWallet"
                          className="flex items-center px-4 py-3 text-text-secondary hover:bg-accent rounded-md transition-colors duration-200"
                          onClick={closeMobileMenu}
                        >
                          <img
                            src={Arrow}
                            className="h-5 w-5 mr-3"
                            alt="Arrow"
                          />
                          Overview
                        </Link>
                        {digitalWalletItems.map((item, index) => (
                          <Link
                            key={index}
                            to={item.link}
                            className="flex items-center px-4 py-3 text-text-secondary hover:bg-accent rounded-md transition-colors duration-200"
                            onClick={closeMobileMenu}
                          >
                            <img
                              src={item.icon}
                              className="h-5 w-5 mr-3"
                              alt={item.title}
                            />
                            <div>
                              <div className="font-medium">{item.title}</div>
                              <div className="text-sm text-text-muted">
                                {item.description}
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>

                  <Separator />
                </>
              )}

              {showBusinessSuiteNavigation && (
                <>
                  <div>
                    <button
                      className={cn(
                        "flex items-center justify-between w-full px-4 py-3 font-semibold text-lg hover:bg-accent rounded-md transition-colors duration-200",
                        isBusinessSuiteNavActive
                          ? "text-[#423E51]"
                          : "text-text-primary"
                      )}
                      onClick={() => setIsBusinessMenuOpen(!isBusinessMenuOpen)}
                    >
                      Business Suite
                      <ChevronRight
                        className={`w-5 h-5 transition-transform ${
                          isBusinessMenuOpen ? "rotate-90" : ""
                        }`}
                      />
                    </button>
                    {isBusinessMenuOpen && (
                      <div className="ml-4 mt-2 space-y-2">
                        {businessSuiteMobileItems.map((item) => {
                          return (
                            <Link
                              key={item.title}
                              to={item.to}
                              state={item.state}
                              className="flex items-center px-4 py-3 text-text-secondary hover:bg-accent rounded-md transition-colors duration-200"
                              onClick={closeMobileMenu}
                            >
                              <img
                                src={item.img}
                                className="h-5 w-5 mr-3 shrink-0"
                                alt=""
                              />
                              <div>
                                <div className="font-medium text-text-primary">
                                  {item.title}
                                </div>
                                <div className="text-sm text-text-muted">
                                  {item.description}
                                </div>
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <Separator />
                </>
              )}

              {featureFlags?.invest && (
                <>
                  <Link
                    to="/invest"
                    className={cn(
                      "block px-4 py-3 font-semibold text-lg hover:bg-accent rounded-md transition-colors duration-200",
                      isActivePath("/invest")
                        ? "text-[#423E51]"
                        : "text-text-primary"
                    )}
                    onClick={closeMobileMenu}
                  >
                    Invest
                  </Link>
                  <Separator />
                </>
              )}

              {featureFlags?.zar8Yield && (
                <>
                  <Link
                    to="/zar8"
                    className={cn(
                      "block px-4 py-3 font-semibold text-lg hover:bg-accent rounded-md transition-colors duration-200",
                      isActivePath("/zar8")
                        ? "text-[#423E51]"
                        : "text-text-primary"
                    )}
                    onClick={closeMobileMenu}
                  >
                    ZAR8
                  </Link>
                  <Separator />
                </>
              )}

              <Link
                to="/support/index"
                className={cn(
                  "block px-4 py-3 font-semibold text-lg hover:bg-accent rounded-md transition-colors duration-200",
                  isActivePath("/support")
                    ? "text-[#423E51]"
                    : "text-text-primary"
                )}
                onClick={closeMobileMenu}
              >
                Support
              </Link>

              {/* {(featureFlags?.buyForex ||
                featureFlags?.sellForex ||
                featureFlags?.withdrawForex ||
                featureFlags?.depositForex) && (
                <>
                  <Separator />
                  <Link
                    to="/forex/forex-dashboard"
                    className={cn(
                      "block px-4 py-3 font-semibold text-lg hover:bg-accent rounded-md transition-colors duration-200",
                      isActivePath("/forex")
                        ? "text-[#423E51]"
                        : "text-text-primary"
                    )}
                    onClick={closeMobileMenu}
                  >
                    Forex
                  </Link>
                </>
              )} */}

              <Separator />

              <div className="space-y-3 pt-4">
                {user && !isLoading && (
                  <div className="px-4 py-3 bg-accent rounded-md">
                    <div className="flex items-center mb-2">
                      <User className="w-4 h-4 mr-2" />
                      <span className="font-medium">
                        {user.name || "Account"}
                      </span>
                    </div>
                    <div className="flex items-center text-sm text-text-secondary">
                      {isVeriffVerified ? (
                        <img
                          src={Verrif}
                          alt="Veriff"
                          width={15}
                          height={15}
                          className="mr-2"
                        />
                      ) : (
                        <img
                          src={unVerrif}
                          alt="Veriff"
                          width={15}
                          height={15}
                          className="mr-2"
                        />
                      )}
                      {userProfile?.veriffDetails?.status
                        ?.charAt(0)
                        .toUpperCase() +
                        userProfile?.veriffDetails?.status?.slice(1)}
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  {process.env.REACT_APP_DARK_MODE_ENABLED === "true" && (
                    <ThemeToggle
                      variant="outline"
                      size="sm"
                      className="flex-1"
                    />
                  )}
                  <Link to="/notifications" className="flex-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full rounded-full border border-bg-neutral"
                      onClick={closeMobileMenu}
                    >
                      <img src={Bell} alt="Bell" className="h-4 w-4 mr-2" />
                      Notifications
                    </Button>
                  </Link>
                  <Link to="/settings/index" onClick={closeMobileMenu}>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-full border border-bg-neutral"
                    >
                      <img src={Settings} alt="Settings" className="h-4 w-4" />
                    </Button>
                  </Link>
                </div>

                {user && !isLoading ? (
                  <Button
                    variant="outline"
                    className="w-full rounded-full"
                    onClick={() => {
                      handleLogout();
                      closeMobileMenu();
                    }}
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </Button>
                ) : !isLoading ? (
                  <Link to="/api/auth/login" onClick={closeMobileMenu}>
                    <Button variant="outline" className="w-full rounded-full">
                      <LogIn className="w-4 h-4 mr-2" />
                      Login
                    </Button>
                  </Link>
                ) : (
                  <div className="text-center py-3">Loading...</div>
                )}
              </div>
            </div>
          </div>
        )}
      </header>
      <div
        className={
          overlayActive ||
          dropdownOpen ||
          dropdownOpenBusiness ||
          isMobileMenuOpen
            ? "fixed inset-x-0 bottom-0 top-[100px] z-40 bg-brand-primary/60 backdrop-blur-[5px]"
            : "hidden"
        }
        onClick={() => {
          setOverlayActive(false);
          setDropdownOpen(false);
          setDropdownOpenBusiness(false);
          setIsMobileMenuOpen(false);
        }}
      ></div>
    </>
  );
}
